import type {
  ICreateClaimBodyV2,
  IListStylesQueryV2,
  IUpdateClaimBodyV2,
} from '@api/types/claims';
import type { IBulkCreateDppBody } from '@api/types/dpp';
import { fetch as fetchRaw } from '@lib/fetch';
import { getDefinedUrlSearchParams } from '@lib/util';
import type {
  AdminUsersListQuery,
  BulkUpdateActionsRequest,
  LinkActionsRequest,
  LinkActionsResponse,
  ListTemplatesQuery,
  ListTemplatesResponse,
  PatchTemplateAnswerBody,
  UnlinkActionsRequest,
  UnlinkActionsResponse,
} from '@retracedgmbh/modules';
import type {
  IAction,
  IActionHistory,
  IActionPatch,
  ICapFinding,
  ICapPlan,
  IListActionsResponse,
} from '@views/capa-v2/types';
import { backend } from '../services';

export type ApiResponse<T> = Promise<{
  data: { data: T };
  error: Error;
  status: number;
}>;

const fetch = (url, ...args) => fetchRaw([backend.url, url], ...args);

// Throws a real Error (preserving the request id for ErrorToast) when the API
// returned an error response, so mutateAsync rejects instead of resolving with
// an error payload. Returns the response unchanged on success.
function throwIfApiError<
  T extends { error?: { message?: string; reqId?: string } | null },
>(response: T, fallbackMessage: string): T {
  if (response.error) {
    throw Object.assign(new Error(response.error.message || fallbackMessage), {
      reqId: response.error.reqId,
    });
  }
  return response;
}

export function requestCode(body) {
  return fetch('/authentication/request-code', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function login(body) {
  return fetch('/authentication/login', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function authenticationByDomain(email: string) {
  const domain = email.split('@')[1];
  if (!domain) throw new Error('Invalid email address');
  return fetch(
    `/authentication/single-sign-on/${encodeURIComponent(domain)}?login_hint=${encodeURIComponent(email)}`,
    {
      method: 'GET',
    },
  );
}

export function exchangeSsoCode(code: string) {
  return fetch(`/authentication/sso-code?code=${encodeURIComponent(code)}`, {
    method: 'GET',
  });
}

export function logout() {
  return fetch('/authentication/logout', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function refreshToken(token: string) {
  return fetchRaw([backend.url, '/authentication/refresh-token'], {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });
}

export function adminFilesList(body) {
  return fetch('/admin/files/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function adminFilesGetById(id) {
  return fetch(`/admin/files/${id}`, {
    method: 'GET',
  });
}

export function adminFilesAIProcessingJobsList(body) {
  return fetch('/admin/files/ai-processing-jobs/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function uploadURLPost(body) {
  return fetch('/system/files/', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export const getCertificatesDrilldownData = body => {
  return fetch('/dashboard-analytics/certification-drilldown/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function adminChatWithFile(body) {
  return fetch('/admin/files/chat-with-file', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function adminFilesAiProcessingJobsCreate(body) {
  return fetch('/admin/files/ai-processing-jobs/create', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export const getAuditsDrilldownData = body => {
  return fetch('/dashboard-analytics/audits-metrics/drill-down/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function getEmailBounceTrackingList(body) {
  return fetch('/system/emails/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getEmailBounceTrackingListDetail(id) {
  return fetch(`/system/emails/${id}`, {
    method: 'GET',
  });
}

export function slcpAssessmentsArchive(body) {
  return fetch('/slcp/assessments/archive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function slcpAssessmentsList(queryParams) {
  return fetch(`/slcp/assessments?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function slcpAssessmentsUnarchive(body) {
  return fetch('/slcp/assessments/archive', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export function adminFilesStatistics() {
  return fetch('/admin/files/statistics', {
    method: 'GET',
  });
}

export const getStylesListData = body => {
  return fetch('/styles/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const checkStyleCodes = (body: {
  codeValues: string[];
  companyId: string;
  isCreatedForNetwork?: boolean;
}) => {
  return fetch('/styles/check-codes', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const adminGetStylesListData = body => {
  return fetch('/admin/styles/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function styleCreate(body) {
  return fetch('/styles', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getStyleTypesList(body) {
  return fetch('/style-types/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getStyleData(id) {
  return fetch(`/styles/${id}`, {
    method: 'GET',
  });
}

export function styleUpdate(id, body) {
  return fetch(`/styles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function bulkCreateDpp(body: IBulkCreateDppBody) {
  return fetch('/dpp', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getStyleTracingOrder(id) {
  return fetch(`/styles/${id}/traced-orders`, {
    method: 'GET',
  });
}

export function getStylePropertiesList(body) {
  return fetch('/style-properties/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function createStyleProperty(body) {
  return fetch('/style-properties', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// Styles Archive/Unarchive
export function archiveStyles(body: { ids: string[] }) {
  return fetch('/styles/archive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function unarchiveStyles(body: {
  ids: string[];
  shouldUnarchiveChildren?: boolean;
}) {
  return fetch('/styles/unarchive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// Variants Archive/Unarchive
export function archiveVariants(body: { ids: string[] }) {
  return fetch('/style-variants/archive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function unarchiveVariants(body: {
  ids: string[];
  shouldUnarchiveChildren?: boolean;
}) {
  return fetch('/style-variants/unarchive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// SKUs Archive/Unarchive
export function archiveSkus(body: { ids: string[] }) {
  return fetch('/variant-skus/archive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function unarchiveSkus(body: { ids: string[] }) {
  return fetch('/variant-skus/unarchive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface BulkUpdateStylesPayload {
  styleIds: string[];
  collection?: Array<{ value: string }>;
  isStoryActive?: boolean;
  isComponent?: boolean;
}

export interface BulkUpdateStylesResponse {
  updatedCount: number;
  variantsUpdatedCount: number;
  skusUpdatedCount: number;
  errors?: Array<{
    styleId: string;
    styleName: string;
    error: string;
  }>;
}

export const bulkUpdateStyles = async (
  payload: BulkUpdateStylesPayload,
): Promise<BulkUpdateStylesResponse> => {
  return fetch('/styles/bulk-update', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export function getStyleCertifications(id, queryParams) {
  return fetch(
    `/styles/${id}/certifications?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getEntityTypeFieldValues(queryParams) {
  return fetch(
    `/style-type-field-values/entity?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function upsertEntityTypeFieldValues(body) {
  return fetch('/style-type-field-values/upsert', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteBatch(id) {
  return fetch(`/batches/${id}`, {
    method: 'DELETE',
  });
}

export function createStyleLink(body) {
  return fetch('/style-links/link', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export const getVariantsListData = body => {
  return fetch('/style-variants/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function getVariantData(id) {
  return fetch(`/style-variants/${id}`, {
    method: 'GET',
  });
}

export function getSkuData(id) {
  return fetch(`/variant-skus/${id}`, {
    method: 'GET',
  });
}

export const getVariantSKUsListData = body => {
  return fetch('/variant-skus/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function variantCreate(body) {
  return fetch('/style-variants', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function variantUpdate(id, body) {
  return fetch(`/style-variants/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function skuCreate(body) {
  return fetch('/variant-skus', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function skuUpdate(id, body) {
  return fetch(`/variant-skus/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export const getBatchesListData = body => {
  return fetch('/batches/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function getBatchData(id) {
  return fetch(`/batches/${id}`, {
    method: 'GET',
  });
}

export function batchCreate(body) {
  return fetch('/batches', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function batchUpdate(id, body) {
  return fetch(`/batches/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function batchBomUpdate(id, body) {
  return fetch(`/batches/${id}/bom`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function batchEntityUpdate(id, body) {
  return fetch(`/batches/${id}/entity`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function materialHeaderCreate(body) {
  return fetch('/material-headers', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
export function materialHeaderUpdate(id, body) {
  return fetch(`/material-headers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function getMaterialHeader(id) {
  return fetch(`/material-headers/${id}`, {
    method: 'GET',
  });
}

export function getMaterialHeadersList(body) {
  return fetch('/material-headers/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteMaterialHeader(body) {
  return fetch('/material-headers/delete', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getMaterialLinesList(body) {
  return fetch('/material-lines/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function materialLineCreate(body) {
  return fetch('/material-lines', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function materialLineUpdate(id, body) {
  return fetch(`/material-lines/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deleteMaterialLine(body) {
  return fetch('/material-lines/delete', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bomHeaderCreate(body) {
  return fetch('/bom-headers', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bomHeaderUpdate(id, body) {
  return fetch(`/bom-headers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function getBomHeader(id) {
  return fetch(`/bom-headers/${id}`, {
    method: 'GET',
  });
}

export function getBomHeadersList(body) {
  return fetch('/bom-headers/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteBomHeader(body) {
  return fetch('/bom-headers/delete', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getBomLinesList(body) {
  return fetch('/bom-lines/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bomLineCreate(body) {
  return fetch('/bom-lines', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bomLineUpdate(id, body) {
  return fetch(`/bom-lines/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deleteBomLine(body) {
  return fetch('/bom-lines/delete', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export const getAssessmentSuggestedCapas = (id, body) => {
  return fetch(`/slcp/assessments/${id}/suggested-capas`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function filesAIProcessingJobsList(body) {
  return fetch('/files/ai-processing-jobs/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function filesAiProcessingJobsCreate(body) {
  return fetch('/files/ai-processing-jobs/create', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
export const networkSlcpAssessmentsList = body => {
  return fetch('/slcp/assessments/network-list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

// BOM Placements endpoints
export function getBomPlacementsList(body) {
  return fetch('/bom-placements/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bomPlacementCreate(body) {
  return fetch('/bom-placements', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function createAuditWithAi(body) {
  return fetch('/capa/process', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function cancelCapaWorkflow(body) {
  return fetch('/capa/process/cancel', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export function capaWorkflowProgress(id) {
  return fetch(`/capa/workflows/${id}`, {
    method: 'GET',
  });
}

export function AIExtractionFeedback(body) {
  return fetch('/ai-extraction-feedback', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function aiFeedback(body: {
  fileId: string;
  assetId: string;
  assetType: string;
  aiProcessType: string;
  vote?: 0 | 1;
  text?: string;
}) {
  return fetch('/ai-feedback', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function capaCapCreate(body) {
  return fetch('/capa/cap-create', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function capaImportActions(body) {
  return fetch('/capa/import-actions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function createFinding(body) {
  return fetch('/capa/findings', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getMyCompanyFindings(queryParams) {
  return fetch(
    `/capa/findings/my-company?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getNetworkFindings(queryParams) {
  return fetch(
    `/capa/findings/network?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getFindingDetails(id: string): ApiResponse<ICapFinding | null> {
  return fetch(`/capa/findings/details/${id}`, {
    method: 'GET',
  });
}

export function patchFinding(id: string, body: Record<string, unknown>) {
  return fetch(`/capa/findings/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function getActionDetails(id) {
  return fetch(`/capa/actions/${id}`, {
    method: 'GET',
  });
}

export function getActionComments(actionId, queryParams) {
  return fetch(
    `/capa/actions/${actionId}/comments?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getActionHistories(
  actionId: string,
  queryParams: { page?: number; count?: number } = {},
): Promise<{
  data: {
    data: IActionHistory[];
    meta: {
      count: number;
      page: number;
      totalCount: number;
      pageSize: number;
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/capa/actions/${actionId}/histories?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function createActionComment(actionId, body) {
  return fetch(`/capa/actions/${actionId}/comments`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function createRemediationRequest(
  actionId: string,
  body: { message: string; attachmentIds: string[] },
) {
  return fetch(`/capa/actions/${actionId}/remediation-requests`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateRemediationRequest(
  actionId: string,
  body: { status: string; requestedChangeMessage?: string },
) {
  return fetch(`/capa/actions/${actionId}/remediation-requests`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function downloadRemediationFiles(actionId: string) {
  return fetch(
    `/capa/actions/${actionId}/remediation-requests/download-files`,
    {
      method: 'GET',
    },
  );
}

export function updateActionComment(actionId, commentId, body) {
  return fetch(`/capa/actions/${actionId}/comments/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function archiveFindings(findingIds) {
  return fetch('/capa/findings/archive', {
    method: 'POST',
    body: JSON.stringify({ findingIds }),
  });
}

export function unarchiveFindings(findingIds) {
  return fetch('/capa/findings/unarchive', {
    method: 'POST',
    body: JSON.stringify({ findingIds }),
  });
}

export function getMyCompanyArchive(queryParams) {
  return fetch(
    `/capa/findings/my-company/archive?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getNetworkArchive(queryParams) {
  return fetch(
    `/capa/findings/network/archive?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getEntityTagsList(queryParams) {
  return fetch(`/entity-tags?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function createEntityTag(body) {
  return fetch('/entity-tags', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateEntityTag(id, body) {
  return fetch(`/entity-tags/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteEntityTag(id) {
  return fetch(`/entity-tags/${id}`, {
    method: 'DELETE',
  });
}

export function linkEntityTags(body) {
  return fetch('/entity-tags/link', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function exportStyles(body) {
  return fetch('/styles/export', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function bulkImportStyles(body: {
  styles: unknown[];
  isComponent?: boolean;
}) {
  const response = await fetch('/styles/bulk-import', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (response.error) {
    throw new Error(response.error.message || 'Import failed');
  }
  return response.data;
}

export async function bomBulkImport(body: { fileId: string }) {
  const response = await fetch('/bom-import/bulk-import', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (response.error) {
    throw new Error(response.error.message || 'Import failed');
  }
  return response.data;
}

export function capaCreatePlan(body) {
  return fetch('/capa/caps', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getCapaCapsList(queryParams) {
  return fetch(`/capa/caps?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function getCapaCapsArchivedList(queryParams) {
  return fetch(`/capa/caps/archive?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function getCapaActionsList(queryParams: {
  count: number;
  page: number;
  sort: string;
  filter: any; // Fix later
  quickFilter: string;
}): Promise<{
  data: IListActionsResponse;
  error: Error;
  status: number;
}> {
  return fetch(`/capa/actions?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function getCapaActionsArchivedList(queryParams: {
  count: number;
  page: number;
  sort: string;
  filter: any; // Fix later
  quickFilter: string;
}): Promise<{
  data: IListActionsResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/capa/actions/archive?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export async function archiveCapas(actionIds, includeLinkedActions = false) {
  return throwIfApiError(
    await fetch(`/capa/archive?includeLinkedActions=${includeLinkedActions}`, {
      method: 'POST',
      body: JSON.stringify({ actionIds }),
    }),
    'Failed to archive',
  );
}

export async function unarchiveCapas(actionIds) {
  return throwIfApiError(
    await fetch('/capa/unarchive', {
      method: 'POST',
      body: JSON.stringify({ actionIds }),
    }),
    'Failed to unarchive',
  );
}

export function tracingAiSummaryBulkCreate(
  ids: string[],
  traceType: string,
  summaryTypes?: string[],
) {
  return fetch('/tracing-ai/bulk-summary', {
    method: 'POST',
    body: JSON.stringify({ ids, traceType, summaryTypes }),
  });
}

export function tracingAiDatasetGenerate(
  summaryId: string,
  type: 'doc_type' | 'doc_content' | 'sc_analysis',
) {
  return fetch(`/tracing-ai/${summaryId}/dataset?type=${type}`, {
    method: 'GET',
  });
}

export function tracingAiSummaryGetById(summaryId: string) {
  return fetch(`/tracing-ai/summary/${summaryId}`, {
    method: 'GET',
  });
}

export function tracingAiSummaryValidate(
  summaryId: string,
  body: {
    tracingType: string;
    tracingId: string;
    summaryType: 'docType' | 'docContent' | 'scAnalysis';
    tracingDocumentId?: string;
    linkId?: string;
  },
) {
  return fetch(`/tracing-ai/summary/${summaryId}/validate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function tracingAiSummaryClear(body: {
  tracingId: string;
  tracingOrder: 'PURCHASE_ORDER' | 'SALES_ORDER';
  summaryType: 'docType' | 'docContent' | 'scAnalysis';
}) {
  return fetch('/tracing-ai/admin/summary/clear', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function tracingAiWorkflowTraceTotal() {
  return fetch('/tracing-ai/summary/workflow-trace/total', {
    method: 'GET',
  });
}

export function getCapaPlanDetails(id: string): ApiResponse<ICapPlan | null> {
  return fetch(`/capa/caps/${id}`, {
    method: 'GET',
  });
}

export function updateCapaPlan(id: string, body) {
  return fetch(`/capa/caps/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function updateCapaAction(id: string, body: IActionPatch) {
  return fetch(`/capa/actions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function linkCapaActions(
  id: string,
  body: LinkActionsRequest,
): ApiResponse<LinkActionsResponse> {
  return fetch(`/capa/actions/${id}/link`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function unlinkCapaActions(
  id: string,
  body: UnlinkActionsRequest,
): ApiResponse<UnlinkActionsResponse> {
  return fetch(`/capa/actions/${id}/unlink`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bulkLinkCapaActionsToCap(body: BulkUpdateActionsRequest) {
  return fetch('/capa/actions/bulk-link-cap', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function bulkUpdateCapaSeverity(
  body: Array<{ id: string; severity: string }>,
) {
  return throwIfApiError(
    await fetch('/capa/actions/bulk-severity', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
    'Failed to update severity',
  );
}

export async function bulkUpdateCapaImpactArea(
  body: Array<{ id: string; impactAreaId: string }>,
) {
  return throwIfApiError(
    await fetch('/capa/actions/bulk-impact-area', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
    'Failed to update impact area',
  );
}

export async function bulkUpdateCapaTags(
  body: Array<{ id: string; tagIds: string[] }>,
) {
  return throwIfApiError(
    await fetch('/capa/actions/bulk-tags', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
    'Failed to update tags',
  );
}

export async function bulkUpdateCapaAssignee(
  body: Array<{ id: string; userId: string; companyId: string }>,
) {
  return throwIfApiError(
    await fetch('/capa/actions/bulk-assignee', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
    'Failed to update assignee',
  );
}

export async function bulkUpdateCapaReporter(body: {
  actionIds: string[];
  add: Array<{ userId: string; companyId: string }>;
  remove: Array<{ userId: string; companyId: string }>;
}) {
  return throwIfApiError(
    await fetch('/capa/actions/bulk-reporter', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
    'Failed to update reporters',
  );
}

export function createActionsFromCap(body: {
  capId: string;
  title: string;
  auditFindingId?: string | null;
  type: 'CA' | 'PA';
}) {
  return fetch('/capa/actions/create-from-cap', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function quickAddActions(body: {
  capId: string | null;
  assignees?: Array<{
    id: string;
    companyId: string;
  }>;
  impactAreaId: string | null;
  priority: string | null;
  title: string;
  companyId: string;
  auditFindingId?: string | null;
  type: 'CA' | 'PA';
}) {
  return fetch('/capa/actions/quick-add-actions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function linkUsersToCapa(body: {
  actionId: string;
  users: {
    userId: string;
    companyId: string;
  }[];
}) {
  return fetch('/capa/link-users', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getSuggestedActions(
  capId: string,
): ApiResponse<{ findings: Array<ICapFinding & { actions: IAction[] }> }> {
  return fetch(`/capa/caps/${capId}/suggested-actions`, {
    method: 'GET',
  });
}

export function capaExport(body: {
  type: 'selected' | 'filtered';
  selected?: {
    entityType: Array<'CAP' | 'ACTION' | 'FINDING'>;
    entityIds: string[];
  };
  filters?: {
    responsibleCompanyIds?: string[];
    creationDateOfFinding?: string;
    sourceType?: string;
    sourceId?: string;
    countries?: string[];
    tierIds?: string[];
    vendorIds?: string[];
    actionStatuses?: string[];
  };
  columns?: {
    CAP?: string[];
    FINDING?: string[];
    ACTION?: string[];
  };
}) {
  return fetch('/capa/export', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getBadges(entityName: string, entityId: string) {
  return fetch(`/${entityName}/${entityId}/badges`, {
    method: 'GET',
  });
}

export function recalculateBadges(_entityName: string, entityId: string) {
  return fetch('/recalculate-badges', {
    method: 'POST',
    body: JSON.stringify({ styleId: entityId }),
  });
}

export function recalculateAllBadges() {
  return fetch('/recalculate-badges', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// Claims V2 endpoints (style-based)
export function claimCreate(body: ICreateClaimBodyV2) {
  return fetch('/claims', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function claimUpdate(id: string, body: IUpdateClaimBodyV2) {
  return fetch(`/claims/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function claimsExport(ids: string[] = []) {
  return fetch('/claims/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
}

export function exportAssessment({
  blueprintId,
  companyIds,
}: {
  blueprintId: string;
  companyIds?: string[];
}) {
  return fetch('/risk-analysis/exports/assessments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assessmentBlueprintId: blueprintId, companyIds }),
  });
}

export function adminClaimCreate(body: ICreateClaimBodyV2) {
  return fetch('/admin/claims', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function adminClaimUpdate(id: string, body: IUpdateClaimBodyV2) {
  return fetch(`/admin/claims/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export const claimListStylesV2 = (
  id: string,
  queryParams?: IListStylesQueryV2,
) => {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return fetch(`/claims/${id}/list-styles${params}`, {
    method: 'GET',
  });
};

export const adminClaimListStylesV2 = (
  id: string,
  queryParams?: IListStylesQueryV2,
) => {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return fetch(`/admin/claims/${id}/list-styles${params}`, {
    method: 'GET',
  });
};

export const styleSupplyChainMappingGet = (
  id: string,
  queryParams?: { isRemovedStatusCheck?: boolean },
) => {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return fetch(`/product-mapping/${id}${params}`, {
    method: 'GET',
  });
};

export const styleSupplyChainNodeDelete = (
  nodeId: string,
  queryParams: { supplyChainId: string },
) => {
  return fetch(
    `/product-mapping/nodes/${nodeId}?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'DELETE',
    },
  );
};

export const getProductMappingLinkInfo = (
  linkId: string,
  supplyChainId: string,
) => {
  return fetch(
    `/product-mapping/link/${linkId}?supplyChainId=${supplyChainId}`,
    {
      method: 'GET',
    },
  );
};

export const updateProductMappingLink = (
  linkId: string,
  body: {
    supplyChainId: string;
    lines: Array<{
      styleId: string;
      quantity: number;
      unit: string;
      secondaryQuantity?: number | null;
      secondaryUnit?: string | null;
    }>;
  },
) => {
  return fetch(`/product-mapping/links/${linkId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

export function companyDocumentsDownload(body: string[]) {
  return fetch('/company-documents/download', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function certificationsDownload(body: string[]) {
  return fetch('/company-certifications/download', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export const styleSupplyChainExtend = (
  supplyChainId: string,
  body: {
    companyId: string;
    featuredFacilityProcessId: string;
    nodeId: string;
    lines?: Array<{
      styleId: string;
      quantity: number;
      unit: string;
      secondaryQuantity?: number | null;
      secondaryUnit?: string | null;
    }>;
  },
) => {
  return fetch(`/product-mapping/${supplyChainId}/extend`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function cpmBulkImport(body: { fileId: string }) {
  return fetch('/product-mapping/bulk-import', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function adminPlatformFeaturesList() {
  return fetch('/admin/platform-features', {
    method: 'GET',
  });
}

export function adminPlatformFeaturesGetById(id: string) {
  return fetch(`/admin/platform-features/${id}`, {
    method: 'GET',
  });
}

/**
 * Lists users across all companies (admin only, delegated-users path — a user
 * appears once per company they belong to). Replaces the legacy
 * `usersGetAll` wrapper that hit `/v3/admin/users/list`.
 */
export function adminUsersList(queryParams: Partial<AdminUsersListQuery>) {
  return fetch(`/admin/users/list?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function adminCompaniesGetListNetwork(id, queryParams?: any) {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return fetch(`/admin/companies/${id}/list-network${params}`, {
    method: 'GET',
  });
}

export function adminCompaniesGetCreditBalance(companyId: string) {
  return fetch(`/admin/companies/${companyId}/credit-balance`, {
    method: 'GET',
  });
}

export function adminCompaniesGetCredits(
  companyId: string,
  queryParams?: {
    page?: number;
    limit?: number;
    sort?: string;
    order?: string;
  },
) {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return fetch(`/admin/companies/${companyId}/credits${params}`, {
    method: 'GET',
  });
}

export function adminCompaniesAddCredits(
  companyId: string,
  body: { amount: number; description?: string },
) {
  return fetch(`/admin/companies/${companyId}/credits`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function startAuditDigitization(auditId: string) {
  return fetch('/audits/digitization', {
    method: 'POST',
    body: JSON.stringify({ auditId }),
  });
}

export function getAuditDigitizationProgress(auditId: string) {
  return fetch(`/audits/digitization/${auditId}`, {
    method: 'GET',
  });
}

export function uploadCertificateDocument(_fileId: string): Promise<void> {
  // TODO: wire up real endpoint
  return Promise.resolve();
}

export function createDigitizedAuditSyncHistory(
  auditId: string,
  body: {
    byCompanyId: string;
    forCompanyId: string;
    onBehalfOfCompanyId: string;
    failedDatapoints: unknown[];
  },
) {
  return fetch(`/audits/digitization/${auditId}/history`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function triggerCertificateAiWorkflow(fileId: string) {
  return fetch(`/certificate-ai/${fileId}`, { method: 'POST' });
}

export function getCertificateAiWorkflow(workflowId: string) {
  return fetch(`/certificate-ai/${workflowId}`, { method: 'GET' });
}

export function cancelCertificateAiWorkflow(body: { workflowId: string }) {
  return fetch('/certificate-ai/cancel', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export function getTemplatesList(
  queryParams: Partial<ListTemplatesQuery>,
): Promise<{ data: ListTemplatesResponse; error: Error; status: number }> {
  return fetch(
    `/companies/templates?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function patchTemplateAnswer(
  templateId: string,
  body: PatchTemplateAnswerBody,
) {
  return fetch(`/companies/templates/${templateId.toLowerCase()}/answer`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function adminPatchTemplateAnswer(
  templateId: string,
  body: PatchTemplateAnswerBody,
) {
  return fetch(
    `/admin/companies/templates/${templateId.toLowerCase()}/answer`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
  );
}
